import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CookiePreferencesModal } from './CookiePreferencesModal';

export const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const [showPreferences, setShowPreferences] = useState(false);
  const [visible, setVisible] = useState(true);

  // Check if consent has already been given
  React.useEffect(() => {
    try {
      const consent = localStorage.getItem('cookie_consent_status');
      if (consent) {
        setVisible(false);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  const handleAcceptAll = () => {
    try {
      localStorage.setItem('cookie_consent_status', 'accepted');
      localStorage.setItem('cookie_consent_essential', 'true');
      localStorage.setItem('cookie_consent_analytical', 'true');
      localStorage.setItem('cookie_consent_marketing', 'true');
    } catch {
      // Ignore storage errors
    }
    setVisible(false);
  };

  const handleRejectAll = () => {
    try {
      localStorage.setItem('cookie_consent_status', 'rejected');
      localStorage.setItem('cookie_consent_essential', 'true');
      localStorage.setItem('cookie_consent_analytical', 'false');
      localStorage.setItem('cookie_consent_marketing', 'false');
    } catch {
      // Ignore storage errors
    }
    setVisible(false);
  };

  const handleSavePreferences = (preferences: {
    essential: boolean;
    analytical: boolean;
    marketing: boolean;
  }) => {
    try {
      localStorage.setItem('cookie_consent_status', 'customized');
      localStorage.setItem('cookie_consent_essential', String(preferences.essential));
      localStorage.setItem('cookie_consent_analytical', String(preferences.analytical));
      localStorage.setItem('cookie_consent_marketing', String(preferences.marketing));
    } catch {
      // Ignore storage errors
    }
    setShowPreferences(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label={t('cookieConsent.title')}
        aria-describedby="cookie-consent-description"
        className="fixed bottom-0 left-0 right-0 z-[100] bg-surface-container-high/95 backdrop-blur-md border-t border-border-subtle shadow-[0_-8px_32px_rgba(0,0,0,0.4)]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="flex-1 min-w-0">
              <p id="cookie-consent-description" className="text-sm text-text-secondary leading-relaxed">
                {t('cookieConsent.description')}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-end gap-3 shrink-0">
              {/* Accept All - Primary button */}
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-accent-primary text-on-primary font-semibold rounded-xl hover:bg-accent-primary/90 hover:shadow-lg hover:shadow-accent-primary/25 hover:-translate-y-0.5 transition-all duration-200 min-h-[48px] min-w-[130px] focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated active:scale-[0.98]"
              >
                {t('cookieConsent.acceptAll')}
              </button>
              {/* Reject All - Secondary outlined button with equal visual weight (GDPR compliance) */}
              <button
                type="button"
                onClick={handleRejectAll}
                className="px-6 py-3 bg-surface-container/80 text-text-primary font-semibold rounded-xl border-2 border-border-subtle hover:bg-surface-container hover:border-border-subtle hover:-translate-y-0.5 transition-all duration-200 min-h-[48px] min-w-[130px] focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated active:scale-[0.98]"
              >
                {t('cookieConsent.rejectAll')}
              </button>
              {/* Manage Preferences - Secondary text style */}
              <button
                type="button"
                onClick={() => setShowPreferences(true)}
                className="px-5 py-3 text-text-secondary font-medium hover:text-text-primary hover:bg-surface-container/50 rounded-lg transition-all duration-200 min-h-[48px] underline underline-offset-2 decoration-border-default hover:decoration-border-strong focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated"
              >
                {t('cookieConsent.managePreferences')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <CookiePreferencesModal
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
};
